import { type ReactNode } from "react";

import { cn } from "#app/utils/misc.tsx";

/**
 * antd's `<Timeline mode="left" items>`: a vertical rail of dots with a
 * connector between them.
 *
 * `color` keeps antd's vocabulary (its preset names plus any CSS colour) so the
 * activity trail's existing status→colour mapping did not have to change.
 */
export type TimelineItem = {
  color?: string;
  dot?: ReactNode;
  children: ReactNode;
};

const PRESET: Record<string, string> = {
  blue: "bg-blue-500",
  green: "bg-emerald-500",
  red: "bg-red-500",
  gray: "bg-zinc-400",
  grey: "bg-zinc-400",
  orange: "bg-orange-500",
  purple: "bg-purple-500",
  processing: "bg-blue-500",
  success: "bg-emerald-500",
  error: "bg-red-500",
  warning: "bg-amber-500",
  default: "bg-zinc-400",
};

export function Timeline({
  items,
  className,
}: {
  items: TimelineItem[];
  className?: string;
}) {
  return (
    <ul className={cn("flex flex-col", className)}>
      {items.map((item, index) => {
        const last = index === items.length - 1;
        const preset = item.color ? PRESET[item.color] : undefined;

        return (
          <li key={index} className="relative flex gap-3 pb-5 last:pb-0">
            {/* Rail. Stops at the last item so the line does not dangle. */}
            {!last ? (
              <span
                aria-hidden
                className="absolute top-3 bottom-0 left-[5px] w-px bg-border"
              />
            ) : null}

            <span className="relative z-10 mt-1.5 flex size-2.5 shrink-0 items-center justify-center">
              {item.dot ?? (
                <span
                  className={cn(
                    "size-2.5 rounded-full ring-2 ring-background",
                    preset ?? "bg-zinc-400",
                  )}
                  // A non-preset colour (a hex, say) falls through to inline.
                  style={preset ? undefined : { backgroundColor: item.color }}
                />
              )}
            </span>

            <div className="min-w-0 flex-1">{item.children}</div>
          </li>
        );
      })}
    </ul>
  );
}

export default Timeline;
